import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../hooks/useAuth';
import { updateReportMetadata } from '../../services/reportService';
import { getSelectableUsers } from '../../services/userService';
import showNotification from '../common/Notification';
import {
  PAGE_TYPES,
  CHANGE_TYPES,
  SUGGESTED_TAGS,
  getPageTypeLabel,
  getChangeTypeLabel,
} from '../../utils/reportTaxonomy';

// Decision enum labels for UI
export const DECISION_OPTIONS = [
  { value: 'pending', label: 'Beklemede' },
  { value: 'apply', label: 'Uygula' },
  { value: 'reject', label: 'Reddet' },
  { value: 'retest', label: 'Tekrar Test' },
];

/**
 * Editable CRO story panel — hypothesis, decision, tags, taxonomy, assignee
 */
const ReportMetadataPanel = ({ reportId, metadata = {}, onUpdated, compact = false }) => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [form, setForm] = useState({
    hypothesis: metadata.hypothesis || '',
    decision: metadata.decision || 'pending',
    decisionNotes: metadata.decisionNotes || '',
    assignedTo: metadata.assignedTo?._id || metadata.assignedTo || '',
    tags: metadata.tags || [],
    pageType: metadata.pageType || '',
    changeType: metadata.changeType || '',
  });
  const [analysts, setAnalysts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Sync when parent report data changes
  useEffect(() => {
    setForm({
      hypothesis: metadata.hypothesis || '',
      decision: metadata.decision || 'pending',
      decisionNotes: metadata.decisionNotes || '',
      assignedTo: metadata.assignedTo?._id || metadata.assignedTo || '',
      tags: metadata.tags || [],
      pageType: metadata.pageType || '',
      changeType: metadata.changeType || '',
    });
  }, [metadata]);

  // Load analyst list for admin/editor
  useEffect(() => {
    if (!canEdit) return;

    const loadAnalysts = async () => {
      try {
        setLoadingUsers(true);
        const users = await getSelectableUsers();
        setAnalysts(users);
      } catch {
        setAnalysts([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadAnalysts();
  }, [canEdit]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = useCallback(async () => {
    if (!reportId || !canEdit) return;

    try {
      setSaving(true);
      const updated = await updateReportMetadata(reportId, {
        hypothesis: form.hypothesis,
        decision: form.decision,
        decisionNotes: form.decisionNotes,
        assignedTo: form.assignedTo || null,
        tags: form.tags,
        pageType: form.pageType || null,
        changeType: form.changeType || null,
      });
      showNotification('CRO bilgileri kaydedildi', 'success');
      onUpdated?.(updated);
    } catch {
      showNotification('Kaydetme sırasında bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  }, [reportId, canEdit, form, onUpdated]);

  const assignedLabel =
    metadata.assignedTo?.username ||
    analysts.find((a) => a.id === form.assignedTo)?.username ||
    '—';

  const decisionLabel =
    DECISION_OPTIONS.find((o) => o.value === (metadata.decision || 'pending'))?.label ||
    'Beklemede';

  return (
    <Accordion
      defaultExpanded={false}
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid #eee',
        borderRadius: '8px !important',
        bgcolor: '#fafafa',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#240B4B' }} />}
        sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#240B4B' }}>
          CRO Story
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: compact ? 2 : 3, pb: compact ? 2 : 3 }}>
      {metadata.status === 'Canlı' && canEdit && (metadata.decision || 'pending') === 'pending' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Testi <strong>Durduruldu</strong> yapmadan önce kararı (Uygula/Reddet/Tekrar Test) kaydedin.
        </Alert>
      )}
      {canEdit ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Hipotez"
            value={form.hypothesis}
            onChange={handleChange('hypothesis')}
            multiline
            minRows={2}
            fullWidth
            size="small"
            placeholder="Bu test neyi doğrulamayı hedefliyor?"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              select
              label="Karar"
              value={form.decision}
              onChange={handleChange('decision')}
              size="small"
              fullWidth
            >
              {DECISION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Sorumlu Analyst"
              value={form.assignedTo}
              onChange={handleChange('assignedTo')}
              size="small"
              fullWidth
              disabled={loadingUsers}
            >
              <MenuItem value="">Atanmadı</MenuItem>
              {analysts.map((analyst) => (
                <MenuItem key={analyst.id} value={analyst.id}>
                  {analyst.username} ({analyst.role})
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Karar Notları"
            value={form.decisionNotes}
            onChange={handleChange('decisionNotes')}
            multiline
            minRows={2}
            fullWidth
            size="small"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              select
              label="Sayfa tipi"
              value={form.pageType}
              onChange={handleChange('pageType')}
              size="small"
              fullWidth
            >
              <MenuItem value="">Seçilmedi</MenuItem>
              {PAGE_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Değişiklik tipi"
              value={form.changeType}
              onChange={handleChange('changeType')}
              size="small"
              fullWidth
            >
              <MenuItem value="">Seçilmedi</MenuItem>
              {CHANGE_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Autocomplete
            multiple
            freeSolo
            options={SUGGESTED_TAGS}
            value={form.tags}
            onChange={(_, value) => setForm((prev) => ({ ...prev, tags: value }))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Etiketler" size="small" placeholder="checkout, cta..." />
            )}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Kaydet'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Hipotez
            </Typography>
            <Typography variant="body2">{metadata.hypothesis || '—'}</Typography>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Sayfa tipi
              </Typography>
              <Typography variant="body2">{getPageTypeLabel(metadata.pageType)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Değişiklik tipi
              </Typography>
              <Typography variant="body2">{getChangeTypeLabel(metadata.changeType)}</Typography>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Karar
              </Typography>
              <Typography variant="body2">{decisionLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Sorumlu
              </Typography>
              <Typography variant="body2">{assignedLabel}</Typography>
            </Box>
          </Box>
          {metadata.decisionNotes && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Karar Notları
              </Typography>
              <Typography variant="body2">{metadata.decisionNotes}</Typography>
            </Box>
          )}
          {metadata.tags?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {metadata.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Box>
          )}
        </Box>
      )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ReportMetadataPanel;
